import { Hub } from "aws-amplify/utils";
import { fetchAuthSession } from "aws-amplify/auth";
import { useSyncExternalStore, useCallback } from "react";

type AuthState = {
  isAuthenticated: boolean;
  isLoading: boolean;
};

// Singleton state for auth status
let authState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
};

// Set of listeners for state changes
const listeners = new Set<() => void>();

// Flag to track if Hub listener is set up
let hubListenerInitialized = false;

// Notify all listeners of state change
const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

// Update auth state and notify listeners
const setAuthState = (newState: Partial<AuthState>) => {
  authState = { ...authState, ...newState };
  notifyListeners();
};

// Check current auth status from Amplify
const checkAuthStatus = async (): Promise<boolean> => {
  try {
    const session = await fetchAuthSession();
    const hasValidTokens = !!(
      session.tokens?.idToken && session.tokens?.accessToken
    );
    return hasValidTokens;
  } catch (error) {
    console.warn("Auth status check failed:", error);
    return false;
  }
};

// Initialize auth state and set up Hub listener (singleton)
const initializeAuthListener = () => {
  if (hubListenerInitialized) return;
  hubListenerInitialized = true;

  // Set up Hub listener for auth events
  Hub.listen("auth", async ({ payload }) => {
    switch (payload.event) {
      case "signedIn":
        setAuthState({ isAuthenticated: true, isLoading: false });
        break;
      case "signedOut":
        setAuthState({ isAuthenticated: false, isLoading: false });
        break;
      case "tokenRefresh":
        // Token refreshed successfully - ensure we're still marked as authenticated
        setAuthState({ isAuthenticated: true, isLoading: false });
        break;
      case "tokenRefresh_failure":
        // Token refresh failed - user needs to re-authenticate
        console.warn("Token refresh failed, user will need to re-authenticate");
        setAuthState({ isAuthenticated: false, isLoading: false });
        break;
      default:
        break;
    }
  });

  // Check initial auth status
  checkAuthStatus().then((isAuthenticated) => {
    setAuthState({ isAuthenticated, isLoading: false });
  });
};

// Subscribe function for useSyncExternalStore
const subscribe = (callback: () => void) => {
  // Initialize Hub listener on first subscription
  initializeAuthListener();

  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
};

// Snapshot function for useSyncExternalStore
const getSnapshot = () => authState;

// Server snapshot (for SSR)
const getServerSnapshot = () => ({
  isAuthenticated: false,
  isLoading: true,
});

/**
 * Hook to get reactive auth status from AWS Amplify.
 * Uses Hub events to stay in sync with Amplify's auth state.
 */
export function useAuthStatus() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const refreshAuthStatus = useCallback(async () => {
    setAuthState({ isLoading: true });
    const isAuthenticated = await checkAuthStatus();
    setAuthState({ isAuthenticated, isLoading: false });
  }, []);

  return {
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    refreshAuthStatus,
  };
}

/**
 * Imperative function to check auth status (for use outside React components)
 */
export const getAuthStatus = async (): Promise<boolean> => {
  return checkAuthStatus();
};
